/* eslint-disable eqeqeq */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Phase 6 Vendor-Port W4 — PHX FORM dependency engine (the parked-MAJOR fix).
 *
 * THE BUG (6e2a deviation note in PlateHeatExchangerForm + parity #324): the PHX
 * form's ~13 dependent selects (temp_rating, seal, configuration, mounting,
 * plate/end-plate/corner mtl+finish+style, plate_orientation, bypass_dmr_mtl/fin)
 * rendered EMPTY with the saved selected display LOST. The React form bound them
 * to `componentCms.<path>` (the plate_heat_exchangers/params/ doc) — but those
 * option lists DON'T live in that doc. Legacy form.js runs its OWN
 * DependenciesHandler graph (mfg/series → ~18 master-list params; mtl→finish;
 * style→flange; size→end_plate+widths; temp→seal) and loads each list from
 * `master/v1.0.0/ahu/plate_heat_exchangers/<filters><param>s/`, rendering the
 * selected label from those lists (with `model.<field>_name` companions as a
 * fallback). This hook reproduces that engine.
 *
 * It REUSES the W3/W4 useDependencyGraph (the same cascading-reset +
 * last-write-wins reducer the selector uses — §53.3 "port DependenciesHandler as
 * a reducer in the phx cut") + phxParamUrl (the same master-list URL builder).
 *
 * ★ DATA-SAFETY: every load here is a GET. The hook NEVER writes. On mount it
 * loads the option lists filtered by the saved mfg/series — read-only. A live
 * parent change re-cascades (still GET-only); the actual model write only
 * happens through the form's existing auto-save when the user picks an option.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useDependencyGraph, type LoadParamsFn } from '../selector/useDependencyGraph';
import {
  PHX_FORM_DEPENDENCY_GRAPH,
  phxParamUrl,
  PHX_PARAMETER_NAMES,
} from '../selector/phxSelectorData';
import type { OptionRow } from '../formKit';

/** The form-side master-list fields → their option lists + a selected-label
 * resolver. The form passes the saved model so we can seed mfg/series + read the
 * `<field>_name` companions. */
export interface PhxFormDeps {
  /** option list for a dependent select (empty until loaded). */
  options: (field: string) => OptionRow[];
  /** the selected label for the saved id: prefer the loaded list row name, then
   * the serializer `<field>_name` companion, else ''. */
  selectedLabel: (field: string, id: unknown) => string;
  /** notify a parent changed (re-cascade) — called from the form onChange. */
  onParentChange: (field: string) => void;
  /** true while any list is loading. */
  isLoading: boolean;
}

/** master-list field → its serializer `*_name` companion (the fallback label). */
const NAME_COMPANION: Record<string, string> = {
  seal_id: 'seal_name',
  configuration_id: 'configuration_name',
  end_plate_style_id: 'end_plate_style_name',
  end_plate_mtl_id: 'end_plate_mtl_name',
  corner_profile_mtl_id: 'corner_profile_mtl_name',
  corner_profile_finish_id: 'corner_profile_finish_name',
  temp_rating_id: 'temp_rating_name',
  // these arrive as a name STRING on the same key (source='<field>.name'):
  mounting_id: 'mounting',
  plate_orientation_id: 'plate_orientation',
  plate_mtl_id: 'plate_mtl',
  plate_finish_id: 'plate_finish',
  end_plate_finish_id: 'end_plate_finish',
  corner_profile_style_id: 'corner_profile_style',
  bypass_dmr_mtl_id: 'bypass_dmr_mtl',
  bypass_dmr_fin_id: 'bypass_dmr_fin',
  temp_rating_id2: 'temp_rating',
};

/** the master-list fields the engine manages (PHX_PARAMETER_NAMES keys). */
const MANAGED_FIELDS = Object.keys(PHX_PARAMETER_NAMES);

export function usePhxFormDeps(
  model: { get: (k: string) => unknown } | null | undefined
): PhxFormDeps {
  const [lists, setLists] = useState<Record<string, OptionRow[]>>({});
  const listsRef = useRef<Record<string, OptionRow[]>>({});
  listsRef.current = lists;

  // a stable snapshot of the parent values the graph filters on.
  const parentValues = useRef<Record<string, unknown>>({});
  const seeded = useRef(false);
  const modelId = model ? (model.get('id') as number | undefined) : undefined;

  // loadParams: GET master/v1.0.0/ahu/plate_heat_exchangers/<filters><param>s/.
  const loadParams = useCallback<LoadParamsFn>(async (field, parents) => {
    const url = phxParamUrl(field, parents, parentValues.current);
    if (!url) return listsRef.current[field] ?? [];
    try {
      const res = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!res.ok) return listsRef.current[field] ?? [];
      const body = await res.json();
      const rows: OptionRow[] = Array.isArray(body) ? body : ((body as any)?.results ?? []);
      setLists((prev) => ({ ...prev, [field]: rows }));
      return rows.map((r: any) => ({ ...r, id: r.id ?? r.value ?? r }));
    } catch {
      return listsRef.current[field] ?? [];
    }
  }, []);

  const getValue = useCallback(
    (field: string) => parentValues.current[field] as any,
    []
  );
  const setValueNoop = useCallback(() => undefined, []); // read-only; never re-pick.

  const graph = useDependencyGraph({
    spec: PHX_FORM_DEPENDENCY_GRAPH,
    loadParams,
    getValue,
    setValue: setValueNoop,
    shouldSetDefaultIfNull: () => false, // read-only: keep the saved value.
  });

  // seed: read mfg/series + the saved ids off the model, then cascade from
  // mfg/series so every dependent list loads filtered by them.
  useEffect(() => {
    if (!model || seeded.current) return;
    const masterPhx = (model.get('master_phx') ?? {}) as Record<string, unknown>;
    let raw: Record<string, unknown> = {};
    try {
      raw = JSON.parse(String(model.get('plate_heat_exchanger_params') ?? '{}'));
    } catch {
      raw = {};
    }
    const pv: Record<string, unknown> = {
      mfg_id: raw.mfg_id ?? (masterPhx as any).mfg_id,
      series_id: raw.series_id ?? (masterPhx as any).series_id,
      size_id: raw.size_id,
    };
    for (const f of MANAGED_FIELDS) pv[f] = model.get(f);
    parentValues.current = pv;
    seeded.current = true;
    // cascade from mfg + series (loads all descendant lists filtered by them).
    if (pv.mfg_id != null) graph.onParentChange('mfg_id');
    if (pv.series_id != null) graph.onParentChange('series_id');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, modelId]);

  const options = useCallback((field: string) => lists[field] ?? [], [lists]);

  const selectedLabel = useCallback(
    (field: string, id: unknown) => {
      if (id == null || id === '' || id === 'None') return '';
      const row = (lists[field] ?? []).find(
        (r: any) => String(r.id ?? r.value) == String(id)
      );
      if (row) return String((row as any).name ?? (row as any).value ?? id);
      // fallback to the serializer `*_name` companion off the model.
      const companion = NAME_COMPANION[field];
      const v = companion && model ? model.get(companion) : null;
      if (v != null && v !== '') return String(v);
      return String(id);
    },
    [lists, model]
  );

  const onParentChange = useCallback(
    (field: string) => {
      // keep the parent snapshot fresh so child filters use the new value.
      parentValues.current = { ...parentValues.current, [field]: model?.get(field) };
      graph.onParentChange(field);
    },
    [graph, model]
  );

  return { options, selectedLabel, onParentChange, isLoading: graph.isCascading };
}
